import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, FolderOpen, PlayCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerField } from '../common/DatePickerField';
import { caseBuilderApi, caseEvidenceApi } from '../../lib/api';
import { apiError } from '../../lib/utils';
import { CaseBuilderStepCard } from './CaseBuilderStepCard';
import { EvidenceOverrideDialog } from '../case/EvidenceOverrideDialog';

const DS_LABEL = { returns: 'Returns dataset', import_declarations: 'Import declarations', exports: 'Export references' };

const ResumePrompt = ({ paused, onContinue, onSwitch }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2 text-sm" data-testid="cb-resume-prompt">
    <div data-testid="cb-resume-message">{paused.message}</div>
    <div className="flex flex-wrap gap-2">
      <Button size="sm" className="h-10 text-xs" onClick={onContinue} data-testid="cb-resume-continue"><PlayCircle className="w-3.5 h-3.5 mr-1" />Continue</Button>
      <Link to={`/cases/${paused.case_id}`} data-testid="cb-resume-view"><Button size="sm" variant="outline" className="h-10 text-xs">View case</Button></Link>
      <Button size="sm" variant="outline" className="h-10 text-xs" onClick={onSwitch} data-testid="cb-resume-switch">Switch case</Button>
    </div>
  </div>
);

const ReminderPanel = ({ reminders, onReview, onOpen }) => (
  <div className="rounded-xl border bg-white p-3 space-y-2 text-sm" data-testid="cb-reminder-panel">
    <div className="flex items-center gap-2 font-semibold"><Clock className="w-4 h-4 text-amber-600" /><span data-testid="cb-reminder-count">{reminders.message}</span></div>
    <ul className="space-y-1 text-xs">
      {reminders.top.map((r) => (
        <li key={r.draft_id} className="flex items-center justify-between gap-2" data-testid={`cb-reminder-${r.draft_id}`}>
          <button type="button" className="font-mono underline" onClick={() => onOpen(r.draft_id)}>{r.case_reference}</button>
          <span className="text-slate-500">needs {DS_LABEL[r.missing_dataset] || r.missing_dataset}</span>
        </li>
      ))}
    </ul>
    <Button size="sm" variant="outline" className="h-10 text-xs" onClick={onReview} data-testid="cb-review-pending">{reminders.cta.label}</Button>
  </div>
);

const DraftList = ({ title, drafts, onOpen, onClose }) => (
  <div className="rounded-xl border bg-white p-3 space-y-2 text-sm" data-testid="cb-draft-list">
    <div className="flex items-center justify-between font-semibold">{title}<button type="button" onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button></div>
    {drafts.length === 0 && <div className="text-xs text-slate-500">No open case drafts.</div>}
    {drafts.map((d) => (
      <button key={d.id} type="button" onClick={() => onOpen(d.id)} className="flex w-full items-center justify-between rounded border px-2 py-1.5 text-left text-xs hover:bg-slate-50" data-testid={`cb-draft-${d.id}`}>
        <span className="font-mono">{d.case_reference}</span>
        <span className="text-slate-500">Step {d.current_step}{d.missing_datasets?.length ? ` · missing ${d.missing_datasets.map((x) => DS_LABEL[x]).join(', ')}` : ''}</span>
      </button>
    ))}
  </div>
);

const StartForm = ({ card, onStart, busy }) => {
  const [f, setF] = useState({ lane: '', case_name: '', date_from: '', date_to: '' });
  return (
    <div className="rounded-xl border border-slate-300 bg-white p-3 space-y-2" data-testid="cb-start-form">
      <div className="text-sm font-semibold">{card.title}</div>
      <div className="text-xs">Workspace: <span className="font-medium">{card.workspace?.name || card.workspace?.id}</span></div>
      <Select value={f.lane} onValueChange={(v) => setF({ ...f, lane: v })}>
        <SelectTrigger className="h-10 text-xs" data-testid="cb-start-lane"><SelectValue placeholder="Lane (required)" /></SelectTrigger>
        <SelectContent className="z-[60]">{['UK', 'EU', 'USA'].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Input className="h-10 text-xs" placeholder="Case name (optional)" value={f.case_name} onChange={(e) => setF({ ...f, case_name: e.target.value })} data-testid="cb-start-name" />
      <div className="flex gap-2">
        <DatePickerField value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} placeholder="From (optional)" testId="cb-start-from" />
        <DatePickerField value={f.date_to} min={f.date_from || undefined} onChange={(v) => setF({ ...f, date_to: v })} placeholder="To (optional)" testId="cb-start-to" />
      </div>
      <div className="text-xs"><span className="font-semibold">Why: </span>{card.why}</div>
      <Button size="sm" className="h-10 text-xs" disabled={!f.lane || busy} onClick={() => onStart(f)} data-testid="cb-action-start">Start case</Button>
    </div>
  );
};

export const MorganCaseBuilder = ({ open, canBuild }) => {
  const [resume, setResume] = useState(null);
  const [state, setState] = useState(null);
  const [view, setView] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [startCard, setStartCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exIndex, setExIndex] = useState(0);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const loadResume = useCallback(() => caseBuilderApi.resume().then((r) => setResume(r.data)).catch(() => setResume(null)), []);
  useEffect(() => { if (open) loadResume(); }, [open, loadResume]);
  useEffect(() => {
    const h = () => { setView('start'); caseBuilderApi.startCard().then((r) => setStartCard(r.data.card)).catch((e) => toast.error(apiError(e, 'Cannot start a case'))); };
    window.addEventListener('morgan:build-case', h);
    return () => window.removeEventListener('morgan:build-case', h);
  }, []);

  const run = async (fn) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.card) { setState(r.data); setView('card'); } return r; }
    catch (e) { toast.error(apiError(e, 'Morgan could not do that')); return null; }
    finally { setBusy(false); }
  };
  const openDraft = (id) => { setExIndex(0); run(() => caseBuilderApi.get(id)); };
  const listDrafts = (v) => caseBuilderApi.drafts().then((r) => { setDrafts(r.data.drafts); setView(v); });

  const onAction = async (a) => {
    const id = state?.draft?.id;
    if (a.id === 'skip') return run(() => caseBuilderApi.skip(id));
    if (a.id === 'goto' || a.id === 'continue_step') return run(() => caseBuilderApi.goto(id, a.step));
    if (a.id === 'run_matching') return run(() => caseBuilderApi.runMatching(id));
    if (a.id === 'apply_matching') return run(() => caseBuilderApi.applyMatching(id));
    if (a.id === 'run_completeness') return run(() => caseBuilderApi.completeness(id));
    if (a.id === 'next_category') { setExIndex(exIndex + 1); return run(() => caseBuilderApi.exceptions(id, exIndex + 1)); }
    if (a.id === 'continue') { setExIndex(0); return run(() => caseBuilderApi.advance(id)); }
    return null;
  };
  const onFile = async (file, kind, itemId) => {
    const d = state.draft;
    if (kind === 'attach_now') {
      setBusy(true);
      try { await caseEvidenceApi.attach(d.case_id, file, { caseItemId: itemId, source: 'morgan' }); toast.success('Evidence attached. Extraction done.'); }
      catch (e) { toast.error(apiError(e, 'Attach failed')); } finally { setBusy(false); }
      return run(() => caseBuilderApi.get(d.id));
    }
    const r = await run(() => caseBuilderApi.upload(d.id, file));
    if (r && r.data.accepted === false) toast.error(r.data.card.upload_error?.message);
    return r;
  };

  if (!canBuild) return null;
  return (
    <div className="space-y-3" data-testid="morgan-case-builder">
      {view !== 'card' && resume?.paused && !view && (
        <ResumePrompt paused={resume.paused} onContinue={() => openDraft(resume.paused.draft_id)} onSwitch={() => listDrafts('switch')} />
      )}
      {resume?.reminders && !view && (
        <ReminderPanel reminders={resume.reminders} onOpen={openDraft} onReview={() => listDrafts('pending')} />
      )}
      {(view === 'switch' || view === 'pending') && (
        <DraftList title={view === 'switch' ? 'Switch case' : 'Cases waiting on datasets'} onClose={() => setView(null)} onOpen={openDraft}
                   drafts={view === 'pending' ? drafts.filter((d) => d.waiting_datasets?.length) : drafts} />
      )}
      {view === 'start' && startCard && <StartForm card={startCard} busy={busy} onStart={(f) => run(() => caseBuilderApi.start(f))} />}
      {view === 'card' && state && (
        <>
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span><FolderOpen className="inline w-3 h-3 mr-1" />Case builder · {state.draft.case_reference}</span>
            <button type="button" className="underline" onClick={() => { setView(null); loadResume(); }} data-testid="cb-pause">Pause</button>
          </div>
          <CaseBuilderStepCard card={state.card} busy={busy} onAction={onAction} onFile={onFile} onOverride={() => setOverrideOpen(true)} />
          <EvidenceOverrideDialog open={overrideOpen} onOpenChange={setOverrideOpen} caseId={state.draft.case_id}
                                  reasons={state.card.readiness?.override_reasons} testPrefix="cb-evidence-override"
                                  onDone={() => run(() => caseBuilderApi.readiness(state.draft.id))} />
        </>
      )}
    </div>
  );
};
